import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  InitialPropertyRow,
  PropertiesProductRow,
} from "./AddProductPropertiesView";

/**
 * SCRUM-2220: the Add Product Properties draft, written as a createSlice like
 * every reducer in this client (SCRUM-1954) and mounted LOCALLY with
 * `useReducer(addProductPropertiesForm.reducer, ...)` — it is the unsaved
 * selection of the screen, not store data. The SCRUM-2127 pattern.
 *
 * It holds ids, not copies of store rows: the category and subcategory rows are
 * looked up in the container during render.
 */
type AddProductPropertiesFormState = {
  showPanel: boolean;
  selectedRow: PropertiesProductRow | null;
  categoryId: string | null;
  subcategoryId: string | null;
  showSubcategories: boolean;
  showProperties: boolean;
  /** The properties enabled for the site's country on the chosen subcategory. */
  selectedProperties: InitialPropertyRow[];
};

const initialState: AddProductPropertiesFormState = {
  showPanel: false,
  selectedRow: null,
  categoryId: null,
  subcategoryId: null,
  showSubcategories: false,
  showProperties: false,
  selectedProperties: [],
};

export const addProductPropertiesForm = createSlice({
  name: "addProductPropertiesForm",
  initialState,
  reducers: {
    panelOpened(state, action: PayloadAction<PropertiesProductRow>) {
      state.selectedRow = action.payload;
      state.showPanel = true;
    },
    panelClosed(state) {
      state.showPanel = false;
    },
    // The category select was cleared: the lists hide, the earlier subcategory
    // and its properties stay what they were (the saved body is unchanged).
    categoryCleared(state) {
      state.showSubcategories = false;
      state.showProperties = false;
      state.categoryId = null;
    },
    categorySelected(state, action: PayloadAction<string | null>) {
      state.categoryId = action.payload;
      state.showSubcategories = true;
      state.showProperties = false;
      state.selectedProperties = [];
      state.subcategoryId = null;
    },
    subcategoryCleared(state) {
      state.showProperties = false;
      state.selectedProperties = [];
      state.subcategoryId = null;
    },
    subcategorySelected(
      state,
      action: PayloadAction<{ subcategoryId: string | null; properties: InitialPropertyRow[] }>
    ) {
      state.selectedProperties = action.payload.properties;
      state.subcategoryId = action.payload.subcategoryId;
      state.showProperties = true;
    },
    propertyChanged(state, action: PayloadAction<{ name: string; value: string }>) {
      state.selectedProperties = state.selectedProperties.map((p) =>
        p.name === action.payload.name ? { ...p, value: action.payload.value } : p
      );
    },
  },
});
